import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useAPI from '../../hooks/useAPI';
import log from '../../helpers/Logger';
import { getRolesPermissions, hasAdminRole } from '../../helpers/Permissions';
import useAppStatus from '../../hooks/useAppStatus';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuth, setIsAuth] = useState(true);
  const [user, setUser] = useState({});
  const [error, setError] = useState(false);
  const { setLoaded } = useAppStatus();

  const { login, logout, getUser, forceLogin } = useAPI();

  const setAuth = (auth) => {
    // Reset loaded status so that the auth flow begins.
    setLoaded(false);
    setIsAuth(auth);
  };

  const handleLogin = (email, password) => login(email, password).then(() => setAuth(true));

  const setActiveAccount = (account) => {
    if (account) localStorage.setItem('activeAccount', JSON.stringify(account));
    else localStorage.removeItem('activeAccount');
  };

  const getActiveAccount = () => {
    const activeAccount = localStorage.getItem('activeAccount');
    return activeAccount ? JSON.parse(activeAccount) : null;
  };

  const handleLogout = () =>
    logout().then((response) => {
      setActiveAccount();
      setAuth(false);
      setUser({});
      // If OIDC logout URL is provided, redirect to it
      if (response.logoutUrl) window.location.href = response.logoutUrl;
    });

  const handleForceLogin = (id) =>
    forceLogin(id).then(() => {
      setAuth(true);
    });

  const fetchUser = () => {
    // Get user data
    // NOTE: React will take care of respecting the order of setUser and setLoaded even if batching
    // so that we are sure that the loaded state will not be set before user data.
    return getUser()
      .then((userData) =>
        setUser({
          ...userData,
          permissions: getRolesPermissions(userData.roles),
          admin: hasAdminRole(userData.roles),
        })
      )
      .catch((e) => {
        log.debug('Failed to retrieve user data', e);

        // If an error happen, set the auth flag to false since we cannot be sure whether the user
        // is authenticated or not.
        setActiveAccount();
        setIsAuth(false);

        if (e.statusCode !== 401) {
          setError(true);
        }
      })
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    if (!isAuth) {
      // No token stored in local storage.
      setLoaded(true);
      return;
    }
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  return (
    <AuthContext.Provider
      value={{
        error,
        isAuth,
        user,
        login: handleLogin,
        forceLogin: handleForceLogin,
        logout: handleLogout,
        setAuth,
        refresh: fetchUser,
        setActiveAccount,
        getActiveAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
